/// <reference types="@types/google.maps" />
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { DEFAULT_MAP_ZOOM } from 'features/store/constants';
import { debounce } from 'lodash-es';
import ReactDOM from 'react-dom';
import { getRandomId } from 'utils';

import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { useLocale } from 'state/intl';
import { primitive } from 'styles/constants';
import { isValidPosition } from 'utils/geolocation';
import noop from 'utils/noop';
import { coordinateDistance } from 'utils/rbi-common';

import mapDefaults from './defaults';
import { MARKER_URIS } from './icons/index.web';
import { createGoogleMapKeyboardInputObserver } from './label-google-map-keyboard-input.web';
import { checkSingleUseMarker, getKeyForMarkerInput } from './marker-utils';
import {
  CenterPoint,
  FitAndCenterFromCoordsFn,
  IMarkerInput,
  IUseMapArgs,
  MarkerTypes,
  PanToFn,
} from './types';

const MapContainer = Box.withConfig({
  backgroundColor: primitive.$mayoEggWhite,
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  height: '100%',
  width: 'full',
});

/**
 * Google Maps implementation
 */
const useMapGoogle = (
  { eventListeners = {}, position, disableControls = false }: IUseMapArgs = {},
  enabled = true
) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markers = useRef<Record<string, IMarkerInput & { marker: google.maps.Marker }>>({});
  const selectedMarkerKey = useRef<string | undefined>(undefined);
  // track when the map container has been loaded into the dom
  const [isMapMounted, setIsMapMounted] = useState(false);
  const domRef = useRef<HTMLDivElement | null>(null);
  const { libraryLoaded } = useGoogleGeolocationLibrary();
  const { region } = useLocale();
  const defaultPosition = mapDefaults[region] ?? mapDefaults.default;
  const startPosition = position
    ? {
        lat: position.lat,
        lng: position.lng,
      }
    : defaultPosition;
  const [center, setCenter] = useState<CenterPoint>(startPosition);
  const [zoom, setZoom] = useState(
    startPosition !== defaultPosition ? defaultPosition.zoom : DEFAULT_MAP_ZOOM
  );

  const mapListeners = useRef<Record<string, google.maps.MapsEventListener[]>>({});

  const addListener = (eventName: string, map: google.maps.Map, cb: Function, id: string) => {
    if (!mapListeners.current[id]) {
      mapListeners.current[id] = [];
    }

    const dereg = map.addListener(eventName, cb);
    mapListeners.current[id] = mapListeners.current[id].concat(dereg);
  };

  const clearListenerCache = (id: string) => {
    const handlers = mapListeners.current[id] || [];
    handlers.forEach(handler => {
      if (typeof handler.remove === 'function') {
        handler.remove();
      }
    });
    mapListeners.current[id] = [];
  };

  /*
   * SETUP MAP
   *
   * This loads the map api internally, sets up some options
   * and begins the whole process.
   *
   * NOTES:
   * - This needs to be an effect
   * - It has required listener clean ups
   * - Can not re-render map created from this hook, will error.
   *
   */

  const prepareMap = useCallback(
    (mapId: string): (() => void) | undefined => {
      if (!libraryLoaded || !window.google?.maps) {
        return;
      }
      const options = {
        center: startPosition,
        styles: [
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
        ],
        disableDefaultUI: true,
        zoom,
        gestureHandling: disableControls ? 'none' : 'greedy',
      } as google.maps.MapOptions;

      const keyboardInputObserver = createGoogleMapKeyboardInputObserver();
      const mapInstance = new window.google.maps.Map(domRef?.current!, options);
      keyboardInputObserver.observeMapContainer(domRef.current);

      const setClampedMapBounds = () => {
        const mapCenter = mapInstance.getCenter() as google.maps.LatLng;

        ReactDOM.unstable_batchedUpdates(() => {
          setCenter({
            lat: mapCenter.lat(),
            lng: mapCenter.lng(),
          });
        });
      };

      window.google.maps.event.addListenerOnce(mapInstance, 'idle', () => {
        const mapZoom = mapInstance.getZoom();
        setZoom(mapZoom!);
        setClampedMapBounds();
      });

      /**
       *  Called by dragstart and zoom_changed listeners to prevent keyboard remaining open on android devices
       *  when dragging/zooming map while search field has focus
       */
      const blurActiveElement = () => {
        const activeElement = document.activeElement as HTMLElement | null;
        activeElement?.blur?.();
      };

      addListener('dragstart', mapInstance, blurActiveElement, mapId);

      addListener('bounds_changed', mapInstance, debounce(setClampedMapBounds, 500), mapId);

      addListener(
        'zoom_changed',
        mapInstance,
        debounce(() => {
          blurActiveElement();
          const mapZoom = mapInstance.getZoom();
          setZoom(mapZoom!);
        }, 500),
        mapId
      );

      Object.keys(eventListeners).forEach(eventName => {
        const handlers = eventListeners[eventName];
        if (typeof handlers === 'function') {
          addListener(eventName, mapInstance, handlers, mapId);
        }
        if (Array.isArray(handlers)) {
          handlers.forEach(handler => {
            addListener(eventName, mapInstance, handler, mapId);
          });
        }
      });
      // Set a minZoom option to prevent the user to zoom out too much
      mapInstance.setOptions({ minZoom: 5 });
      setMap(mapInstance);

      return () => keyboardInputObserver.disconnect();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableControls, eventListeners, zoom, libraryLoaded]
  );

  const clearMarkers = useCallback(() => {
    Object.values(markers.current).forEach(({ marker }) => {
      marker.setMap(null);
    });
    markers.current = {};
  }, []);

  useEffect(() => {
    // Don't initialize if disabled
    if (!enabled) {
      return;
    }

    const shouldLoad = !!domRef.current;

    if (!shouldLoad) {
      return;
    }

    const mapId = getRandomId();

    const cleanupKeyboardInputObserver = prepareMap(mapId);

    return () => {
      cleanupKeyboardInputObserver?.();
      clearListenerCache(mapId);
      clearMarkers();
    };
    /*
     * NOTE:
     * - Only run when we know the map container has been loaded in the dom ~ isMapMounted
     * - isMapMounted starts as false and is only set to true when we have a dom ref
     * - And the domRef.current will be set
     */
  }, [enabled, isMapMounted, libraryLoaded, clearMarkers]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLoadedIntoDom = useCallback((ref: HTMLDivElement) => {
    if (!domRef.current && ref) {
      domRef.current = ref;
      setIsMapMounted(true);
    }
  }, []);

  const panTo = useCallback<PanToFn>(
    async newPosition => {
      if (!map) {
        return;
      }

      if (isValidPosition(newPosition)) {
        map.panTo(newPosition);
      }

      if (map.getZoom()! < DEFAULT_MAP_ZOOM && newPosition !== defaultPosition) {
        map.setZoom(DEFAULT_MAP_ZOOM);
      }
    },
    [defaultPosition, map]
  );

  useEffect(() => {
    if (map && isValidPosition(position)) {
      panTo(position);
    }
    // We recreate a new object above each render, which makes a simple object
    // compare fail. We only want to run this effect when the actual values change
  }, [map, position?.lat, position?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Creates a marker for a given type and location. If marker
   * already placed on map, updates accordingly.
   */
  const createMarker = useCallback(
    (markerInput: IMarkerInput): string => {
      if (!libraryLoaded || !map) {
        return '';
      }

      const { type, location, onPress, isSelected, title } = markerInput;
      const key = getKeyForMarkerInput(markerInput, checkSingleUseMarker(type));

      if (!isValidPosition(location)) {
        return key; // bail if location is not known
      }

      // This updates the marker if it has already been added to map.
      // Set selected marker key
      selectedMarkerKey.current = isSelected ? key : selectedMarkerKey.current;

      const existingMarker = markers.current[key]?.marker;
      if (existingMarker) {
        existingMarker.setMap(map);
        existingMarker.setTitle(title ?? '');
        markers.current[key].title = title;

        window.google.maps.event.clearInstanceListeners(existingMarker);
        window.google.maps.event.addListener(existingMarker, 'click', onPress!);

        // Update Icon
        if (markers.current[key].type !== type || markers.current[key].isSelected !== isSelected) {
          markers.current[key].type = type;
          markers.current[key].isSelected = isSelected;
          const updatedIcon = isSelected
            ? MARKER_URIS[MarkerTypes.StoreOpenSelected]
            : MARKER_URIS[type];
          existingMarker.setIcon(updatedIcon);
        }

        // Update position
        existingMarker.setPosition(location);
        return key;
      }

      const icon = isSelected ? MARKER_URIS[MarkerTypes.StoreOpenSelected] : MARKER_URIS[type];
      const marker = new window.google.maps.Marker({
        position: location,
        map,
        clickable: !!onPress,
        icon,
        title,
        visible: type !== MarkerTypes.UserManual,
      });
      if (onPress) {
        window.google.maps.event.addListener(marker, 'click', onPress);
      }

      markers.current[key] = { id: key, marker, type, location, isSelected, onPress };
      return key;
    },
    [libraryLoaded, map]
  );

  const handleOnMarkerListItemSelect = useCallback(
    (markerInput: IMarkerInput) => {
      //Unselect current selected marker
      const selectedMarker = selectedMarkerKey.current
        ? markers.current[selectedMarkerKey.current]
        : undefined;
      selectedMarker &&
        createMarker({
          ...selectedMarker,
          type: selectedMarker.type,
          location: selectedMarker.location,
          onPress: selectedMarker.onPress,
          isSelected: false,
        });
      // Select a new marker
      createMarker({ ...markerInput, isSelected: true });
      panTo(markerInput.location);
    },
    [createMarker, panTo]
  );

  const createMarkerList = useCallback(
    (markerList: IMarkerInput[]) => {
      const nextMarkerKeys = new Set(
        markerList.map(marker => getKeyForMarkerInput(marker, checkSingleUseMarker(marker.type)))
      );

      Object.entries(markers.current).forEach(([key, value]) => {
        if (!nextMarkerKeys.has(key)) {
          value.marker.setMap(null);
          delete markers.current[key];
          if (selectedMarkerKey.current === key) {
            selectedMarkerKey.current = undefined;
          }
        }
      });

      markerList.forEach(markerInput => {
        createMarker({
          ...markerInput,
          onPress: () => {
            handleOnMarkerListItemSelect(markerInput);
            markerInput.onPress?.();
          },
        });
      });
    },
    [createMarker, handleOnMarkerListItemSelect]
  );

  /**
   * Adjusts map viewport to ensure all points are visible
   * @param points - Array of map points to fit in viewport
   *
   * Creates a bounding box that includes all points and adds padding (expandFactor)
   * to ensure points aren't too close to the edges of the map
   */
  const fitAndCenterFromCoords = useCallback<FitAndCenterFromCoordsFn>(
    (coordsArray: CenterPoint[]) => {
      if (!libraryLoaded || !map || coordsArray.length === 0) {
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      coordsArray.forEach(point => bounds.extend(point));
      const distanceBetweenPoints = coordinateDistance(
        { latitude: coordsArray[0].lat, longitude: coordsArray[0].lng },
        { latitude: coordsArray[1].lat, longitude: coordsArray[1].lng }
      );

      map.setOptions({
        maxZoom: 25,
        minZoom: 1,
      });

      map.fitBounds(bounds, 60);

      if (distanceBetweenPoints === 0) {
        map.setZoom(15);
      }
    },
    [libraryLoaded, map]
  );

  const mapElement = React.createElement(MapContainer, { ref: onLoadedIntoDom });

  const updateMarkerPosition = useCallback((markerKey: string, location: CenterPoint) => {
    markers.current[markerKey]?.marker?.setPosition(location);
  }, []);

  // Return default/empty result when disabled
  if (!enabled) {
    return {
      center: { lat: 0, lng: 0 },
      createMarker: () => '',
      createMarkerList: noop,
      clearMarkers: noop,
      fitAndCenterFromCoords: noop,
      map: null,
      panTo: noop as PanToFn,
      updateMarkerPosition: noop,
      zoom: DEFAULT_MAP_ZOOM,
    };
  }

  return {
    center,
    createMarker,
    createMarkerList,
    clearMarkers,
    fitAndCenterFromCoords,
    map: mapElement,
    panTo,
    updateMarkerPosition,
    zoom,
  };
};

export default useMapGoogle;
